import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ExternalLink, Save, MessageCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
const AdminForumManagement = () => {
  const [forumLink, setForumLink] = useState('');
  const queryClient = useQueryClient();
  const {
    data: currentLink,
    isLoading
  } = useQuery({
    queryKey: ['forum-link'],
    queryFn: async () => {
      // Use raw SQL query to access the new admin_settings table
      const {
        data,
        error
      } = await supabase.rpc('check_admin_simple', {
        user_email: (await supabase.auth.getUser()).data.user?.email || ''
      });
      if (!data) {
        throw new Error('Admin access required');
      }

      // Now fetch the forum link using a raw query
      const {
        data: settingsData,
        error: settingsError
      } = await supabase.from('admin_settings').select('value').eq('key', 'community_forum_link').maybeSingle();
      if (settingsError && settingsError.code !== 'PGRST116') {
        throw settingsError;
      }
      const link = settingsData?.value || '';
      setForumLink(link);
      return link;
    }
  });
  const updateForumLinkMutation = useMutation({
    mutationFn: async (newLink: string) => {
      const {
        error
      } = await supabase.from('admin_settings').upsert({
        key: 'community_forum_link',
        value: newLink,
        updated_at: new Date().toISOString()
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['forum-link']
      });
      toast.success('Community forum link updated successfully');
    },
    onError: error => {
      console.error('Error updating forum link:', error);
      toast.error('Failed to update forum link');
    }
  });
  const handleSave = () => {
    if (!forumLink.trim()) {
      toast.error('Please enter a valid forum link');
      return;
    }

    // Basic URL validation
    try {
      new URL(forumLink);
      updateForumLinkMutation.mutate(forumLink);
    } catch {
      toast.error('Please enter a valid URL');
    }
  };
  const testLink = () => {
    if (forumLink) {
      window.open(forumLink, '_blank', 'noopener,noreferrer');
    }
  };
  if (isLoading) {
    return <div className="text-white">Loading forum settings...</div>;
  }
  return <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Community Forum Management</h2>
      </div>

      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center">
            <MessageCircle className="w-5 h-5 mr-2" />
            Forum Link Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="forum-link" className="text-gray-300">
              Community Forum URL
            </Label>
            <p className="text-sm text-gray-400 mb-2">
              Enter the URL where users will be redirected when they click "Join Community Forum"
            </p>
            <Input id="forum-link" value={forumLink} onChange={e => setForumLink(e.target.value)} placeholder="https://discord.gg/your-server or https://forum.yoursite.com" className="bg-white/10 border-white/20 text-white" />
          </div>

          <div className="flex space-x-2">
            <Button onClick={handleSave} disabled={updateForumLinkMutation.isPending} className="bg-green-600 hover:bg-green-700">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
            
            {forumLink && <Button onClick={testLink} variant="outline" className="border-white/20 text-white bg-white/0">
                <ExternalLink className="w-4 h-4 mr-2" />
                Test Link
              </Button>}
          </div>

          <div className="mt-6 p-4 bg-blue-600/10 border border-blue-500/20 rounded-lg">
            <h4 className="text-blue-300 font-medium mb-2">Usage Instructions</h4>
            <ul className="text-sm text-gray-300 space-y-1">
              <li>• Enter your Discord server invite link (e.g., https://discord.gg/your-server)</li>
              <li>• Or use your custom forum URL (e.g., https://forum.yoursite.com)</li>
              <li>• Users will be redirected to this link when they click "Join Community Forum"</li>
              <li>• Make sure the link is publicly accessible</li>
            </ul>
          </div>

          {currentLink && <div className="mt-4 p-4 bg-white/10 border border-white/20 rounded-lg">
              <h4 className="text-white font-medium mb-2">Current Active Link</h4>
              <p className="text-gray-300 break-all">{currentLink}</p>
            </div>}
        </CardContent>
      </Card>
    </div>;
};
export default AdminForumManagement;